import React, { JSX, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import LzXBaseField from '../BaseField/BaseField';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { debounce } from '@mui/material/utils';
import { LzTFunctionSimple, LzTOpenObject } from '@lz/typings';
import { lzFIsArray } from '@lz/services/Type';
import useLzFApiCall from '@lz/services/Api';
import LzMOption from '@lz/models/commons/Option';

import './AutocompleteField.scss';

export interface LzPAutocompleteField {
    fluid?: boolean;
    required?: boolean;
    lazyLoad?: boolean;
    compact?: boolean;
    placeholder?: string;
    label?: string;
    name?: string;
    icon?: JSX.Element;
    id?: string;
    className?: string;
    style?: React.CSSProperties;
    value?: string | number | LzMOption | null;
    valueType?: 'string' | 'number' | 'option';
    getOptionLabel?: (option: LzMOption) => string;
    /**
     * If defined, then selected option's text is sent back in the onChange 3rd arg, with key=displayTextKey and value as it is updated.
     */
    displayTextKey?: string;
    displayText?: string;
    size?: 'small' | 'medium';
    variant?: 'outlined' | 'filled' | 'standard';
    options?: LzMOption[];
    refTableName?: string;
    refTableIdField?: string;
    refTableTitleField?: string;
    refFilterFieldKeys?: string[];
    modelToFilter?: LzTOpenObject;
    queryVarName?: string;
    maxLimit?: number;
    noFirstOption?: boolean;
    firstOptionValue?: string | number;
    firstOptionText?: string;
    onChange?: (name: string, value: string | number | LzMOption | null, extras?: object) => void;
    filterOptionsFunction?: (options: LzMOption[]) => LzMOption[];
    ref?: React.Ref<unknown>;
    onBlur?: LzTFunctionSimple;
}

const LzXAutocompleteField = (props: LzPAutocompleteField) => {
    if (typeof props.value === 'undefined') {
        return <CircularProgress />;
    }

    const buildCurrentValueFromProps = () =>
        props.value instanceof LzMOption ? props.value : new LzMOption({ value: props.value, text: props.displayText });

    const loadingFromDBAttempted = React.useRef(false);

    const [options, setOptions] = useState<LzMOption[] | null>(typeof props.options === 'undefined' ? null : props.options);
    const [filteredOptions, setFilteredOptions] = useState<LzMOption[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [opened, setOpened] = useState(false);
    const [currentValue, setCurrentValue] = useState<LzMOption | null>(buildCurrentValueFromProps());
    const [inputValue, setInputValue] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const lzApiCall = useLzFApiCall();

    const setFitleredOptionsWithFiltering = (options: LzMOption[] | null) => {
        if (options === null) {
            setFilteredOptions(null);
        } else {
            if (typeof props.filterOptionsFunction === 'undefined') {
                setFilteredOptions(options);
            } else {
                setFilteredOptions(props.filterOptionsFunction(options));
            }
        }
    };

    const loadOptions = async (searchQuery?: string) => {
        if (loading) {
            return;
        }
        if (props.refTableName && props.refTableName != '') {
            setLoading(true);
            setErrorMessage('');

            let params = { max: props.maxLimit || 0, [props.queryVarName || 'q']: searchQuery || inputValue };

            // Only send filters to server if its lazyLoad (and not instant load)
            if (props.lazyLoad && props.refFilterFieldKeys && props.modelToFilter) {
                const filters: LzTOpenObject = {};
                for (const filterFieldKey of props.refFilterFieldKeys) {
                    if (typeof props.modelToFilter[filterFieldKey] !== 'undefined') {
                        filters[filterFieldKey] = props.modelToFilter[filterFieldKey];
                    }
                }
                params = Object.assign({}, params, filters);
            }

            const data = await lzApiCall('options/' + props.refTableName.replace(/_/g, '-'), params);

            // Load data if call was successful
            if (data) {
                const newOptions = LzMOption.parseArray(
                    data,
                    props.refTableIdField || 'id',
                    props.refTableTitleField || 'name',
                    props.refFilterFieldKeys,
                );
                setOptions(newOptions);
                if (props.lazyLoad) {
                    setFitleredOptionsWithFiltering(newOptions);
                }
            } else {
                setErrorMessage('Cannot load options, please view logs...');
            }
            setLoading(false);
        }
    };

    useEffect(() => {
        setCurrentValue(buildCurrentValueFromProps());
    }, [props.value]);

    useEffect(() => {
        // If options not provided
        if (typeof props.options === 'undefined') {
            setOptions(null);

            if (props.lazyLoad) {
                if (opened) {
                    loadOptions();
                }
            } else {
                if (!loadingFromDBAttempted.current) {
                    loadOptions();
                    loadingFromDBAttempted.current = true;
                }
            }
        }

        // If options provided then use them
        else {
            setOptions(props.options);
        }
    }, [props.options]);

    const debounceLoadOptions = useMemo(() => debounce((searchQuery: string) => loadOptions(searchQuery), 400), []);

    useEffect(() => {
        if (opened) {
            if (props.lazyLoad) {
                loadOptions();
            }
        }
    }, [opened]);

    useEffect(() => {
        if (opened) {
            if (props.lazyLoad) {
                debounceLoadOptions(inputValue);
            }
        }
    }, [inputValue]);

    // useLzEffectAfterMount() does not work if the select field is in some content that is toggled for display (like Dialog)
    useEffect(() => {
        // If options not set then filtered options are null as well
        if (!options) {
            setFitleredOptionsWithFiltering(props.lazyLoad ? [] : null);
            return;
        }

        // If its in lazyLoad mode then filtered options are the ones that come from server, so just exit
        if (props.lazyLoad) {
            return;
        }

        // Build filtered options
        const temp: LzMOption[] = [];
        for (const option of options) {
            if (props.refFilterFieldKeys && props.refFilterFieldKeys.length > 0 && props.modelToFilter) {
                for (const filterFieldKey of props.refFilterFieldKeys) {
                    if (
                        typeof props.modelToFilter[filterFieldKey] === 'undefined' ||
                        typeof option[filterFieldKey] === 'undefined' ||
                        props.modelToFilter[filterFieldKey] === option[filterFieldKey]
                    ) {
                        temp.push(option);
                    }
                }
            } else {
                temp.push(option);
            }
        }
        // console.log('Filtered-Opts', temp);
        setFitleredOptionsWithFiltering(temp);
    }, [props.refFilterFieldKeys, props.modelToFilter, options]);

    const isReady = filteredOptions && lzFIsArray(filteredOptions);

    const theName = props.name || '';
    const theId = props.id || 'id_' + theName;

    const handleOnChange = (val: LzMOption | null): void => {
        let updated: number | string | LzMOption = val || '';
        if ('number' === props.valueType) {
            updated = +(val?.value || 0);
        } else if ('string' === props.valueType) {
            updated = '' + (val?.value || 0);
        }
        if (props.onChange) {
            props.onChange(
                theName,
                updated,
                typeof props.displayTextKey !== 'undefined' && props.displayTextKey !== '' ? { [props.displayTextKey]: val?.text || '' } : undefined,
            );
        }
    };

    const compareOptWithVal = (option: LzMOption, value: number | string | LzMOption | null) => {
        // If value is defined to be `number` in props, then do a direct match
        if ('number' === props.valueType) {
            return option.value === value;
        }

        // If value is defined to be `string` in props, then do a direct match
        if ('string' === props.valueType) {
            return option.value === value;
        }

        // By default we assume it as an `option`

        // If the value is an option, only then we compare it
        if (value instanceof LzMOption) {
            return option.value === value?.value;
        }
        // Else we return not matched
        return false;
    };

    // console.log('AC-Props', props);
    // console.log('VALUES', inputValue, currentValue);

    return (
        <LzXBaseField labelShrink id={theId} fluid={props.fluid} required={props.required} compact={props.compact} icon={props.icon} name={props.name}>
            {!isReady && (
                <div className={'lz-select' + (props.compact ? ' lz-compact' : '')}>
                    {loading && <CircularProgress />}
                    {errorMessage !== '' && (
                        <Alert variant="filled" color="warning">
                            {errorMessage}
                        </Alert>
                    )}
                </div>
            )}

            {isReady && (
                <Autocomplete filterSelectedOptions open={opened} ref={props.ref}
                    onOpen={() => setOpened(true)}
                    onClose={() => setOpened(false)}
                    fullWidth={props.fluid}
                    size={props.size || 'small'}
                    id={theId}
                    options={filteredOptions}
                    getOptionLabel={typeof props.getOptionLabel !== 'undefined' ? props.getOptionLabel : (option) => option?.text || ''}
                    isOptionEqualToValue={compareOptWithVal}
                    value={currentValue}
                    className={'lz-autocomplete ' + (props.className ? props.className : '')}
                    style={props.style || {}}
                    filterOptions={(x) => x}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={props.label}
                            variant={props.variant}
                            required={props.required}
                            name={props.name || ''}
                            placeholder={props.placeholder || ''}
                            autoComplete="password"
                            slotProps={{
                                inputLabel: { shrink: true },
                            }}
                            className={
                                'lz-base-field lz-input-field ' +
                                (props.compact ? 'lz-compact ' : '') +
                                (props.className || '')
                            }
                        />
                    )}
                    slotProps={{
                        popper: {
                            style: { width: 'fit-content' },
                            placement: 'top-start',
                        }
                    }}
                    onChange={(_, val, _r) => handleOnChange(val)}
                    onInputChange={(_, newInputValue) => {
                        setInputValue(newInputValue);
                    }}
                    onBlur={props.onBlur}
                />
            )}
        </LzXBaseField>
    );
};

export default LzXAutocompleteField;
